<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
    size="full"
    side="bottom"
  >
    <template #header>
      <DrawerHeader
        :title="$t('admin.navigation.filter_and_sort.label')"
        v-bind="{
          variant: 'tertiary',
          type: 'gray',
          size: 'xl',
          leadingAction: {
            label: $t('components.buttons.clear_all'),
            iconOnly: false,
            event: 'clear-all',
          },
          onClearAll: () => clearAll(),
        }"
        class="bg-primary max-md:rounded-none border-b border-secondary"
      >
      </DrawerHeader>
    </template>
    <template #content>
      <div class="overflow-y-auto flex flex-col gap-4 grow">
        <Drawer
          :options="sortOptions"
          :modelValue="sortingValues"
          @update:sorting="handleSortingChange"
        />
        <div class="flex flex-col">
          <template v-if="buildingId && type !== 'recurring_tasks'">
            <template v-if="type === 'assets'">
              <Tree
                :key="'space'"
                :type="'space'"
                :buildings="buildings"
                :title="t('components.filters.spaces_or_assets.spaces')"
                :showIncludeAssets="false"
                :modelValue="linkedToSelection"
                @update:modelValue="handleChangeFilter('linked_to', $event)"
                @update:includeAssets="handleUpdateIncludeAssets($event)"
              />
              <Tree
                :key="'asset_group'"
                :type="'asset_group'"
                :buildings="buildings"
                :title="t('components.filters.spaces_or_assets.asset_groups')"
                :modelValue="assetGroupsSelection"
                @update:modelValue="handleChangeFilter('asset_groups', $event)"
                @update:includeAssets="handleUpdateIncludeAssets($event)"
              />
            </template>
            <template v-else>
              <Tree
                v-for="filter in linkedToFilters"
                :key="filter.type"
                :type="filter.type"
                :buildings="buildings"
                :title="filter.title"
                :modelValue="linkedToSelection"
                :includeAssets="includeAssets"
                @update:modelValue="handleChangeFilter('linked_to', $event)"
                @update:includeAssets="handleUpdateIncludeAssets($event)"
              />
            </template>
          </template>
          <template v-else>
            <Tree
              v-for="filter in linkedToFilters"
              :key="filter.type"
              :type="filter.type"
              :buildings="buildings"
              :title="filter.title"
              :modelValue="linkedToSelection"
              :showIncludeAssets="false"
              :includeAssets="false"
              @update:modelValue="handleChangeFilter('linked_to', $event)"
              @update:includeAssets="handleUpdateIncludeAssets($event)"
            />
          </template>
          <Accordion
            :type="type"
            :filterStates="filterStates"
            :filters="filterOptions"
            @update:filters="handleUpdateFilters"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div
        class="flex gap-3 bg-primary justify-end flex-col border-t py-4 px-6 md:py-0 md:px-0 md:flex-row md:border-transparent"
      >
        <Button
          v-if="canSaveView"
          size="xl"
          variant="secondary"
          type="gray"
          class="grow"
          :label="t('components.buttons.save_as')"
          :disabled="!isFiltered"
          @click="toggleAddViewDialog"
        />

        <Button
          v-if="!currentView.default"
          :disabled="!isFiltered"
          size="xl"
          variant="secondary"
          type="gray"
          class="grow"
          :label="t('components.buttons.update_view')"
          @click="handleUpdateView"
        />
        <DialogClose
          :as="Button"
          size="xl"
          variant="primary"
          class="grow"
          :label="t('components.buttons.show_results')"
        />
      </div>
    </template>
  </Dialog>

  <Add
    :open="isAddViewDialogOpen"
    :name="name"
    :errorMessage="errorMessage"
    @update:name="name = $event"
    @update:open="toggleAddViewDialog"
    @cancel="toggleAddViewDialog"
    @submit="handleAdd"
  />
</template>

<script setup>
// External dependencies
import { storeToRefs } from "pinia";
import { pickBy, isNil, isEmpty, first } from "lodash";
import { computed, ref, inject } from "vue";
import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";

// Shared components
import { Dialog, DialogClose } from "@/SharedComponents/Dialog";
import { Accordion } from "@/SharedComponents/Filters/Drawer/Accordion";
import { Drawer } from "@/SharedComponents/Sorting/Drawer";
import { DrawerHeader } from "@/SharedComponents/Drawer";
import { Tree } from "@/SharedComponents/Filters/Drawer/Tree";
import { Button } from "@/SharedComponents/Buttons";
import { Add } from "@/SharedComponents/PageHeader/Partials/CustomViewDialogs";

// Utility functions and composables
import { useFilterOptions, FilterType } from "@/SharedComponents/Filters";
import { useSortOptions } from "@/SharedComponents/Sorting";
import { useSorting } from "@/Composables/useSorting";
import { useSearch } from "@/Composables/useSearch";
import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";

import {
  useAddCustomFilesView,
  useUpdateCustomFilesView,
} from "@/Composables/useFilesViews";
import {
  useAddCustomTasksView,
  useUpdateCustomTasksView,
} from "@/Composables/useTasksViews";

import { toast } from "@/SharedComponents/Toaster";

// Emit event to update the 'open' prop
const emit = defineEmits(["update:open", "updateView", "addView"]);

// Emitter
const emitter = inject("emitter");

// Define props for the component
const props = defineProps({
  open: { type: Boolean, required: false },
  type: { type: String, required: true },
  views: { type: Array, required: true },
  currentView: { type: Object, required: true },
  buildingId: { type: String, required: true },
});

const { DefaultView } = useDefaultTasksViews();

const canSaveView = computed(() => {
  if (props.type === "assets" || props.type === "recurring_tasks") {
    return false;
  }

  return (
    props.currentView.default &&
    props.currentView.id !== DefaultView.ARCHIVED.id
  );
});

// Fetch buildings data
const { isLoading, data: buildingsData } = useSites();

// Compute buildings based on loading state and buildingId
const buildings = computed(() => {
  if (isLoading.value) {
    return [];
  }

  if (props.type === "recurring_tasks") {
    return buildingsData.value;
  }

  return props.buildingId
    ? buildingsData.value.filter((building) => building.id === props.buildingId)
    : buildingsData.value;
});

const buildingIds = computed(() => {
  if (props.type === "recurring_tasks") {
    return [];
  }

  return props.buildingId
    ? [props.buildingId]
    : buildings.value.map((building) => building.id);
});

// Initialize sorting store and extract relevant properties
const sortingStore = useSorting(props.type);
const { sortingValues } = storeToRefs(sortingStore);
const { handleSortingChange, handleClearSorting } = sortingStore;

// Initialize filter store and extract relevant properties
const filterStore = useFilters(props.type);
const { handleClearFilters, handleChangeFilter } = filterStore;
const { filterStates } = storeToRefs(filterStore);

// Search
const searchStore = useSearch(props.type);
const { handleClearSearch } = searchStore;

const page = usePage();
const includeAssets = ref(false);
const name = ref("");
const isAddViewDialogOpen = ref(false);

// Get filter and sort options
const { filterOptions } = useFilterOptions(buildingIds.value, page, props.type);
const { sortOptions } = useSortOptions(props.type);

// Initialize i18n
const { t } = useI18n();

// Define linked to filters
const linkedToFilters = ref([
  {
    title: t("components.filters.spaces_or_assets.spaces"),
    type: "space",
  },
  {
    title: t("components.filters.spaces_or_assets.assets"),
    type: "asset",
  },
]);

// Handle updating the includeAssets value
const handleUpdateIncludeAssets = (value) => {
  includeAssets.value = value;
};

// Handle updating filters
const handleUpdateFilters = ({ values, handle }) => {
  handleChangeFilter(
    handle,
    handle === FilterType.VISIBILITY || handle === FilterType.TASK_TYPE
      ? first(values)
      : values
  );
};

// Clear all filters and sorting
function clearAll() {
  // Clear searchs
  handleClearSearch();

  // Clear all sorting options
  handleClearSorting();

  // Clear all applied filters
  handleClearFilters();

  // Set includeAssets to false, excluding assets from the selection
  includeAssets.value = false;
}

// Compute the linked to selection based on filter states
const linkedToSelection = computed(() => {
  return filterStates.value["linked_to"]
    ? filterStates.value["linked_to"]
    : { buildings: [], children: [] };
});

const assetGroupsSelection = computed(() => {
  return filterStates.value["asset_groups"]
    ? { buildings: [], children: filterStates.value["asset_groups"] }
    : { buildings: [], children: [] };
});

const errorMessage = computed(() => {
  if (
    props.views.some(
      (view) => view.name.toLowerCase() === name.value.toLowerCase()
    )
  ) {
    return t("admin.views.errors.duplicate");
  }

  return "";
});

function handleUpdateView() {
  if (props.type === "tasks") {
    updateCustomTasksView({
      id: props.currentView.id,
      name: props.currentView.name,
      filterStates: filterStates.value,
    });
  }

  if (props.type === "files") {
    updateCustomFilesView({
      id: props.currentView.id,
      name: props.currentView.name,
      filterStates: filterStates.value,
    });
  }
}

const toggleAddViewDialog = () => {
  if (isAddViewDialogOpen.value) {
    isAddViewDialogOpen.value = false;
    name.value = "";
  } else {
    isAddViewDialogOpen.value = true;
  }
};

const { createCustomTasksView } = useAddCustomTasksView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.create.success"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";

    // Emit add view
    emit("addView", view);
  },
  onError: () => {
    // Show the error toast
    toast.success(t("toasts.views.create.error"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";
  },
});

const { createCustomFilesView } = useAddCustomFilesView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.create.success"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";

    // Emit add view
    emit("addView", view);
  },
  onError: () => {
    // Show the error toast
    toast.success(t("toasts.views.create.error"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";
  },
});

const { updateCustomTasksView } = useUpdateCustomTasksView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.update.success"));

    // Emit update view
    emit("updateView", view);
  },
  onError: (err) => {
    toast.error(err.response.data.message || t("toasts.views.update.error"));
  },
});

const { updateCustomFilesView } = useUpdateCustomFilesView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.update.success"));

    // Emit update view
    emit("updateView", view);
  },
  onError: (err) => {
    toast.error(err.response.data.message || t("toasts.views.update.error"));
  },
});

const isFiltered = computed(() => {
  let currentViewFilters =
    props.currentView.filters || props.currentView.criteria;

  return (
    JSON.stringify(
      pickBy(filterStates.value, (value) => !isNil(value) && !isEmpty(value))
    ) !==
    JSON.stringify(
      pickBy(currentViewFilters, (value) => !isNil(value) && !isEmpty(value))
    )
  );
});

const handleAdd = () => {
  if (props.type === "tasks") {
    createCustomTasksView({
      name: name.value,
      tenantId: props.buildingId,
      filterStates: filterStates.value,
    });
  }

  if (props.type === "files") {
    createCustomFilesView({
      name: name.value,
      tenantId: props.buildingId,
      filterStates: filterStates.value,
    });
  }
};

emitter.on("reset-filters", () => {
  clearAll();
});
</script>
